import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOMeta from '@/components/base/SEOMeta';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import ArticleCard from '@/components/base/ArticleCard';
import { JsonLD } from '@/components/base/SchemaOrg';
import type { ArticleSchema, BreadcrumbListSchema } from '@/components/base/SchemaOrg';
import { articles } from '@/mocks/articles';

const articleDetailImageMap: Record<string, string> = {
  'article-tsinghua-1': 'https://readdy.ai/api/search-image?query=Beautiful%20Chinese%20university%20campus%20scene%20with%20young%20female%20students%20in%20modern%20laboratory%20setting%2C%20warm%20lighting%2C%20academic%20atmosphere%2C%20editorial%20photography&width=1200&height=600&seq=detail-article-tsinghua-1&orientation=landscape',
  'article-pku-1': 'https://readdy.ai/api/search-image?query=Elegant%20female%20college%20student%20in%20traditional%20Chinese%20library%2C%20warm%20sunlight%2C%20intellectual%20atmosphere%2C%20soft%20portrait%2C%20serene%20academic%20setting&width=1200&height=600&seq=detail-article-pku-1&orientation=landscape',
  'article-whu-1': 'https://readdy.ai/api/search-image?query=Cherry%20blossom%20trees%20in%20full%20bloom%20at%20traditional%20Chinese%20university%2C%20pink%20petals%20falling%2C%20historic%20architecture%2C%20spring%20season%2C%20dreamy%20atmosphere&width=1200&height=600&seq=detail-article-whu-1&orientation=landscape',
  'article-memories-1': 'https://readdy.ai/api/search-image?query=Young%20college%20students%20walking%20on%20campus%20path%20during%20golden%20hour%2C%20nostalgic%20atmosphere%2C%20sunlight%20through%20leaves%2C%20warm%20photography&width=1200&height=600&seq=detail-article-memories-1&orientation=landscape',
  'article-rules-1': 'https://readdy.ai/api/search-image?query=University%20auditorium%20stage%20with%20young%20female%20performers%20in%20elegant%20dresses%2C%20spotlight%2C%20formal%20event%2C%20grand%20setting%2C%20professional%20stage&width=1200&height=600&seq=detail-article-rules-1&orientation=landscape',
  'article-zju-1': 'https://readdy.ai/api/search-image?query=Graceful%20female%20dancer%20performing%20traditional%20Chinese%20classical%20dance%2C%20flowing%20silk%20costume%2C%20elegant%20movements%2C%20stage%20lighting%2C%20artistic&width=1200&height=600&seq=detail-article-zju-1&orientation=landscape',
  'article-xmu-1': 'https://readdy.ai/api/search-image?query=Seaside%20university%20campus%20with%20traditional%20Chinese%20architecture%20by%20ocean%2C%20golden%20sunset%2C%20palm%20trees%2C%20coastal%20scenery%2C%20picturesque&width=1200&height=600&seq=detail-article-xmu-1&orientation=landscape',
  'article-graduation-1': 'https://readdy.ai/api/search-image?query=University%20graduates%20in%20caps%20and%20gowns%20on%20campus%20lawn%2C%20golden%20hour%2C%20joyful%20celebration%2C%20throwing%20caps%2C%20nostalgic&width=1200&height=600&seq=detail-article-graduation-1&orientation=landscape',
  'article-psych-1': 'https://readdy.ai/api/search-image?query=Diverse%20group%20of%20university%20students%20in%20circle%20discussion%2C%20modern%20campus%2C%20intellectual%20atmosphere%2C%20warm%20indoor%20lighting&width=1200&height=600&seq=detail-article-psych-1&orientation=landscape',
  'article-fudan-1': 'https://readdy.ai/api/search-image?query=Young%20female%20medical%20student%20in%20white%20coat%2C%20modern%20hospital%20setting%2C%20professional%20and%20compassionate%2C%20editorial%20portrait&width=1200&height=600&seq=detail-article-fudan-1&orientation=landscape',
  'article-nju-1': 'https://readdy.ai/api/search-image?query=Historic%20Chinese%20university%20campus%20in%20autumn%2C%20golden%20plane%20tree%20leaves%2C%20vintage%20architecture%2C%20warm%20sunlight%2C%20romantic&width=1200&height=600&seq=detail-article-nju-1&orientation=landscape',
  'article-freshman-1': 'https://readdy.ai/api/search-image?query=Freshman%20students%20arriving%20at%20beautiful%20university%20campus%20gate%2C%20autumn%2C%20excited%20expressions%2C%20warm%20sunlight%2C%20welcoming&width=1200&height=600&seq=detail-article-freshman-1&orientation=landscape',
};

export default function ArticlePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <i className="ri-error-warning-line text-5xl text-foreground-300 block mb-4"></i>
            <p className="text-foreground-700 text-lg mb-4">文章不存在</p>
            <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryNameMap: Record<string, string> = {
    'xiaohua-pingxuan': '校花评选',
    'xiaohua-gushi': '校花故事',
    'meili-xiaoyuan': '美丽校园',
    'qingchun-jiyi': '青春记忆',
  };

  const relatedArticles = articles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 3);
  const detailImage = articleDetailImageMap[article.imageSeq] || articleDetailImageMap['article-tsinghua-1'];

  const articleSchema: ArticleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    author: { '@type': 'Person', name: article.author },
    publisher: { '@type': 'Organization', name: '校花文化网' },
    url: `https://xiaohua-culture.com/article/${article.id}`,
  };

  const breadcrumbSchema: BreadcrumbListSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://xiaohua-culture.com' },
      { '@type': 'ListItem', position: 2, name: categoryNameMap[article.category], item: `https://xiaohua-culture.com/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  const breadcrumbItems = [
    { label: categoryNameMap[article.category] || article.category, href: `/category/${article.category}` },
    { label: article.title },
  ];

  const paragraphs = article.content.split('\n\n').filter(Boolean);

  return (
    <>
      <SEOMeta title={article.title} description={article.excerpt} keywords={article.tags.join(',')} canonicalUrl={`https://xiaohua-culture.com/article/${article.id}`} ogType="article" />
      <JsonLD data={articleSchema as unknown as Record<string, unknown>} />
      <JsonLD data={breadcrumbSchema as unknown as Record<string, unknown>} />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <Breadcrumbs items={breadcrumbItems} />
          </div>
        </div>

        <article className="py-8 md:py-12 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-4xl mx-auto">
            <div className="mb-8">
              <div className="flex items-center flex-wrap gap-2 mb-3">
                <span className="inline-block px-2.5 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-md">
                  {categoryNameMap[article.category]}
                </span>
                {article.tags.map((tag) => (
                  <span key={tag} className="inline-block px-2.5 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-md">
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center flex-wrap gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line text-sm"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line text-sm"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line text-sm"></i>
                  {article.readTime}
                </span>
              </div>
            </div>

            <div className="relative h-64 md:h-80 rounded-lg overflow-hidden mb-10">
              <img
                src={detailImage}
                alt={article.title}
                className="w-full h-full object-cover object-top rounded-lg"
              />
            </div>

            <div className="prose max-w-none text-foreground-800 leading-relaxed">
              {paragraphs.map((para, idx) => {
                if (para.startsWith('## ')) {
                  return <h2 key={idx} className="font-heading text-xl font-semibold text-foreground-900 mt-8 mb-3">{para.replace('## ', '')}</h2>;
                }
                if (para.startsWith('### ')) {
                  return <h3 key={idx} className="font-heading text-lg font-semibold text-foreground-900 mt-6 mb-2">{para.replace('### ', '')}</h3>;
                }
                if (para.startsWith('|')) {
                  const rows = para.split('\n').filter(Boolean);
                  const isHeader = rows[1] && rows[1].includes('---');
                  return (
                    <div key={idx} className="overflow-x-auto my-4">
                      <table className="w-full text-sm border-collapse">
                        <tbody>
                          {rows.filter((_, i) => i !== 1 || !isHeader).map((row, ri) => {
                            const cells = row.split('|').filter(c => c.trim());
                            const CellTag = isHeader && ri === 0 ? 'th' : 'td';
                            return (
                              <tr key={ri} className={ri === 0 && isHeader ? 'bg-background-100' : 'border-b border-background-200'}>
                                {cells.map((cell, ci) => (
                                  <CellTag key={ci} className={`px-4 py-2 text-left ${isHeader && ri === 0 ? 'font-medium text-foreground-900' : 'text-foreground-700'}`}>
                                    {cell.trim()}
                                  </CellTag>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                }
                return <p key={idx} className="text-foreground-700 leading-relaxed mb-4">{para}</p>;
              })}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <Link to={`/category/${article.category}`} className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors">
                <i className="ri-arrow-left-line"></i> {t('article.backToList')}
              </Link>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-14 bg-background-100">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">{t('article.relatedArticles')}</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((a) => (
                  <ArticleCard key={a.id} {...a} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}